import {
  Divider,
  makeStyles,
  Theme,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import {
  Button,
  IconButton,
  Modal,
  ProfileEditIcon,
  Typography,
  WifiCam,
} from '@thingsw/pitta-design-system';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import HelpIcon from '@material-ui/icons/HelpOutline';
import CloseIcon from '@material-ui/icons/Close';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../features/store';
import { USER } from '../../features/User/slice';
import { openToast } from '../../features/Toast/slice';
import { detect } from 'detect-browser';
import {
  BlackvueLightColors,
  ICameraInfo,
  jwtAxiosInst,
  LightColors,
  Webviewer,
} from '@thingsw/pitta-modules';
import React from 'react';
import Input from '@thingsw/pitta-design-system/dist/components/Input';
import ProfileCrop from '../ProfileCrop';
import ReactDOM from 'react-dom';
import { PERMISSION } from '../../features/Permission/slice';
import clsx from 'clsx';
import { THEME } from '../../features/Theme/slice';
import { getRenewalCloudConnectivityIcon } from '../../utils/Camera';

const useStyles = makeStyles((theme: Theme) => ({
  content: {
    display: 'flex',
    flexDirection: 'column',
  },
  edit: {
    background: '#FFFFFF',
    borderRadius: '50%',
    width: 28,
    height: 28,
    border: '2px solid #D4D4D5',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'absolute',
    right: 0,
    bottom: 0,
    cursor: 'pointer',
  },
  input: {
    width: '100%',
  },
  rbutton: {
    minWidth: '100%',
    [theme.breakpoints.up(Webviewer.mobile)]: {
      minWidth: 100,
    },
  },
  typo: {
    height: 41,
    display: 'flex',
    alignItems: 'center',
  },
  closeStyle: {
    [theme.breakpoints.up(Webviewer.mobile)]: {
      top: 9,
      right: 9,
    },
  },
  modalContentDiv: {
    padding: '11px 24px 16px',
    [theme.breakpoints.up(Webviewer.mobile)]: {
      padding: '11px 24px 24px',
    },
  },
  modalBtnDiv: {
    '& > div': {
      marginLeft: '0px !important',
      display: 'contents',
    },
    padding: '24px 24px 24px',
    [theme.breakpoints.up(Webviewer.mobile)]: {
      padding: '16px 24px 24px',
    },
  },
  modalDiv: {
    margin: 16,
    maxWidth: 400,
    position: 'relative',
  },
  changeProfileImageModal: {
    width: 343,
    padding: '16px 0',
    border: 'none',
  },
  contentNoPadding: {
    padding: 0,
  },
  helpBtn: {
    '&:hover': {
      color: (props: any) => props.blackvueColors.primary['2'],
    },
  },
  activeHelpBtn: {
    color: (props: any) => props.blackvueColors.primary['7'],
  },
  disabledEditIcon: {
    '& path': {
      fill: LightColors.primary['3'],
    },
  },
  lottieDiv: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    right: 0,
    left: 0,
    zIndex: 0,
    overflow: 'hidden',
    borderRadius: '16px',
  },
  whiteIcon: {
    '& path': {
      fill: (props: any) => props.colors.primary['0'],
    },
  },
  blackIcon: {
    '& path': {
      fill: (props: any) => props.colors.primary['1'],
    },
  },
  resetBtnHover: {
    '&:hover': {
      color: (props: any) => `${props.blackvueColors.primary['2']} !important`,
    },
  },
}));

const DEFAULT_PROFILE_IMAGE = '/images/default-profile-light.png';

const helpComponent = ({ children }: { children: React.ReactNode }) => {
  // root 요소를 선택
  const rootElement = document.getElementById('root');

  // rootElement가 존재하는지 확인 후, Portal을 생성합니다.
  return rootElement ? ReactDOM.createPortal(children, rootElement) : null;
};

interface EditProfileModalProps {
  open: boolean;
  loading?: boolean;
  camera?: ICameraInfo;
  onClose: () => void;
  onSuccess: () => void;
}

export const EditProfileModal = ({
  open,
  camera,
  onClose,
  onSuccess,
}: EditProfileModalProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const {
    permission: { renameCamPerm },
  } = useSelector((state: RootState) => state[PERMISSION]);

  const inputRef = useRef<HTMLInputElement>(null);

  const { colors, blackvueColors, secondaryColors } = useSelector(
    (state: RootState) => state[THEME]
  );

  const theme = useTheme() as Theme;
  const mobile = useMediaQuery(theme.breakpoints.down(Webviewer.mobile));

  const classes = useStyles({ colors, blackvueColors, secondaryColors });

  const { email } = useSelector((state: RootState) => state[USER]);

  const [profileType, setProfileType] = useState<'img' | 'lottie'>();
  const [profileImage, setProfileImage] = useState<string>('');
  const [tempProfileImage, setTempProfileImage] = useState<string>('');
  const [file, setFile] = useState<any>();
  const [loading, setLoading] = useState(false);
  const [devName, setDevName] = useState('');
  const [camNameLengthErr, setCamNameLengthErr] = useState(false);
  const [
    isOpenChangeProfileImageModal,
    setIsOpenChangeProfileImageModal,
  ] = useState(false);
  const [isShowHelp, setIsShowHelp] = useState(false);
  const [isShowWithoutSaveModal, setIsShowWithoutSaveModal] = useState(false);
  const helpButtonRef = useRef<HTMLButtonElement>(null);
  const [helpPosition, setHelpPosition] = useState({ x: 0, y: 0 });

  const browser = detect();

  const isChangeProfile = useMemo(() => {
    let isChangeImage = false;
    let isChangeName = false;

    if (profileImage !== camera?.profile_img_v2) {
      isChangeImage = true;
    }

    if (camera?.profile_img_v2.includes('.json') && profileImage === '') {
      isChangeImage = false;
    }

    if (devName !== camera?.dev_name) {
      isChangeName = true;
    }

    return isChangeImage || isChangeName;
  }, [camera?.dev_name, camera?.profile_img_v2, devName, profileImage]);

  const handleCloseModal = () => {
    if (isChangeProfile) {
      setIsShowWithoutSaveModal(true);
    } else {
      onClose();
      setCamNameLengthErr(false);
    }
  };

  useEffect(() => {
    if (camera?.profile_img_v2) {
      if (camera.profile_img_v2.indexOf('.json') > -1) {
        setProfileType('lottie');
      } else {
        setProfileType('img');
      }
    }
  }, [camera?.profile_img_v2]);

  const getProfileImageFile = useCallback(async () => {
    if (!camera?.profile_img_v2) return;
    const res = await fetch(camera?.profile_img_v2);
    const blob = await res.blob();
    const file = new File([blob], 'profileImage.jpg', { type: blob.type });
    setFile(file);
  }, [camera?.profile_img_v2]);

  useEffect(() => {
    if (!open) {
      setProfileImage('');
      setFile(undefined);
    } else {
      console.log('set Profile');

      // mantis - 11103, 모달 오픈되면 카메라 이미지 넣어주도록 수정 (Leehj)
      setDevName(camera?.dev_name ?? '');
      setProfileImage(camera?.profile_img_v2 || '');
      if (!camera?.profile_img_v2?.includes('.json')) {
        getProfileImageFile();
      }
    }
  }, [camera, getProfileImageFile, open]);

  const updatePosition = useCallback(() => {
    if (helpButtonRef.current) {
      const rect = helpButtonRef.current.getBoundingClientRect();
      setHelpPosition({
        x: rect.x,
        y: rect.y,
      });
    }
  }, [helpButtonRef]);

  useEffect(() => {
    if (isShowHelp) {
      // 초기 위치 계산
      updatePosition();
    }
  }, [isShowHelp, updatePosition]);

  useEffect(() => {
    // resize 이벤트에 대해 updatePosition을 호출하는 이벤트 리스너 추가
    window.addEventListener('resize', updatePosition);

    // cleanup: 컴포넌트가 언마운트될 때 이벤트 리스너 제거
    return () => {
      window.removeEventListener('resize', updatePosition);
    };
  }, [updatePosition]);

  // 모달 닫을 때 help 닫기
  useEffect(() => {
    return () => {
      setIsShowHelp(false);
    };
  }, []);

  const handleFileChange = useCallback(async (event) => {
    const fileObj = event.target.files[0];

    console.log(`fileObj`, fileObj);
    if (!fileObj) {
      return;
    }
    setFile(fileObj);

    const reader = new FileReader();
    reader.onload = (e) => {
      setTempProfileImage(e.target?.result as string);
    };
    reader.readAsDataURL(fileObj);

    // 👇️ reset file input
    event.target.value = null;
    setIsOpenChangeProfileImageModal(true);
  }, []);

  useEffect(() => {
    if (devName.match(/\s/g)?.length === devName.length) {
      setCamNameLengthErr(true);
    } else if (devName.trim().length < 5 || devName.trim().length > 32) {
      setCamNameLengthErr(true);
    } else {
      setCamNameLengthErr(false);
    }
  }, [devName]);

  const handleSave = useCallback(async () => {
    try {
      setLoading(true);
      // mantis - 11070, 5~32자 기준에 맞지 않는 경우 에러메세지 출력 (Leehj)
      if (devName.match(/\s/g)?.length === devName.length) {
        setCamNameLengthErr(true);
      } else if (devName.trim().length < 5 || devName.trim().length > 32) {
        setCamNameLengthErr(true);
      } else {
        const form = new FormData();
        form.append(
          'body',
          JSON.stringify({
            email,
            psn: camera?.psn,
            devName,
            version: '2',
          })
        );
        let link = '/Account/CameraProfile/default';
        if (file) {
          link = '/Account/CameraProfile';
          form.append('file', file);
        }
        const resp = await jwtAxiosInst.post(link, form, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        if (resp.data.resultcode === 'BC_ERR_OK') {
          //문구 확인 필요
          dispatch(openToast({ message: 'Your profile has been saved.' }));
          onSuccess();
          setCamNameLengthErr(false);
        } else {
          //문구 확인 필요
          dispatch(openToast({ message: 'Failed to save camera profile.' }));
        }
      }
    } catch (e) {
    } finally {
      setLoading(false);
    }
  }, [camera?.psn, devName, dispatch, email, file, onSuccess]);

  const handleResetProfileImage = useCallback(() => {
    setProfileImage('');
    setFile(undefined);
  }, []);

  const helpContentMarkup = useMemo(() => {
    return (
      <div
        style={{
          padding: 16,
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginBottom: 8,
          }}
        >
          <Typography variant='H6'>{t('Edit Image')}</Typography>
          <IconButton onClick={() => setIsShowHelp(false)}>
            <CloseIcon />
          </IconButton>
        </div>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            marginBottom: 16,
          }}
        >
          <Typography variant='Body' htmlColor={LightColors.primary['2']}>
            {t('Set your image_')}
          </Typography>

          <Typography variant='Body' htmlColor={LightColors.primary['2']}>
            {t('*Only jpg and_')}
          </Typography>
        </div>
        <img
          width={310}
          height={200}
          src='/images/image_change.gif'
          alt='change profile guide gif'
        />
      </div>
    );
  }, [t]);

  const getConnectionInfo = useCallback(
    (camera: ICameraInfo) => {
      if (camera.communication_identifier === 'Wifi') {
        return t('Using Wi-Fi Hotspot');
      } else {
        if (!camera.iccid) {
          return 'SIM';
        } else {
          const formattedIccid =
            camera.iccid.match(/.{1,4}/g)?.join(' ') || camera.iccid;
          if (camera.iccid.startsWith('89464283')) {
            return `BlackVue SIM ICCID: ${formattedIccid}`;
          } else {
            return `SIM ICCID: ${formattedIccid}`;
          }
        }
      }
    },
    [t]
  );

  return (
    <Modal
      open={open}
      onClose={handleCloseModal}
      onClickPositive={handleSave}
      loading={loading}
      className={classes.modalDiv}
      content={
        <>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: 32,
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <Typography category='Default' variant='H6'>
                {t('Camera Profile')}
              </Typography>

              <IconButton
                onClick={() => {
                  setIsShowHelp((prev) => !prev);
                }}
                className={clsx(classes.helpBtn, {
                  [classes.activeHelpBtn]: isShowHelp,
                })}
                ref={helpButtonRef}
              >
                <HelpIcon />
              </IconButton>
            </div>
            <div>
              <IconButton>
                <CloseIcon onClick={handleCloseModal} />
              </IconButton>
            </div>
          </div>
          <input
            style={{ display: 'none' }}
            ref={inputRef}
            type='file'
            accept='image/jpeg,image/png'
            onChange={handleFileChange}
          />
          <div
            style={{
              width: '100%',
              height: 80,
              borderRadius: 6,
              marginBottom: 8,
              position: 'relative',
            }}
          >
            {profileType !== 'lottie' && (
              <div
                className={classes.lottieDiv}
                style={{
                  background:
                    'linear-gradient(180deg, rgba(0, 0, 0, 0.0425) 0%, rgba(19, 19, 28, 0.85) 100%)',
                  width: '100%',
                }}
              ></div>
            )}
            <img
              src={
                profileImage === '' || profileType === 'lottie'
                  ? DEFAULT_PROFILE_IMAGE
                  : profileImage
              }
              alt='profile'
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                borderRadius: 6,
              }}
            />
            <div
              style={{
                position: 'absolute',
                left: 14,
                bottom: 8,
                display: 'flex',
                gap: 8,
                alignItems: 'center',
              }}
            >
              {camera && camera.reg_category === 'wifi' && (
                <WifiCam
                  className={clsx({
                    [classes.whiteIcon]: profileType === 'img',
                    [classes.blackIcon]: profileType === 'lottie',
                  })}
                />
              )}

              {camera &&
                camera.reg_category === 'cloud' &&
                getRenewalCloudConnectivityIcon(camera, blackvueColors)}

              <Typography
                variant='SmallBold'
                htmlColor={
                  profileType === 'img'
                    ? colors.primary['0']
                    : colors.primary['1']
                }
              >
                {camera?.dev_name ?? ''}
              </Typography>
            </div>
            <div
              style={{
                position: 'absolute',
                right: 4,
                top: 4,
              }}
            >
              <IconButton
                disabled={!renameCamPerm}
                className={clsx({
                  [classes.disabledEditIcon]: !renameCamPerm,
                })}
                onClick={() => {
                  inputRef.current?.click();
                }}
              >
                <ProfileEditIcon />
              </IconButton>
            </div>
          </div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'end',
              marginBottom: 16,
            }}
          >
            <Button
              disabled={!renameCamPerm}
              style={{
                color: !renameCamPerm
                  ? BlackvueLightColors.primary['3']
                  : BlackvueLightColors.primary['7'],
              }}
              className={classes.resetBtnHover}
              onClick={handleResetProfileImage}
              variant='text'
            >
              {t('Set as default image')}
            </Button>
          </div>
          <div
            style={{
              marginBottom: 24,
            }}
          >
            <Input
              inputID='devName'
              className={classes.input}
              label={t('Camera name')}
              disabled={!renameCamPerm}
              value={devName}
              // mantis - 11074, 한글 입력시 33자도 입력되는 이슈 수정(Leehj)
              onChange={(e) => {
                setDevName((e.target.value = e.target.value.slice(0, 32)));
                // mantis - 11105, ios 한글 Buffer 이슈수정 (Leehj)
                if (browser?.os === 'iOS') {
                  document.getElementById('hiddenInput')?.focus();
                  document.getElementById('devName')?.focus();
                }
              }}
              // mantis - 10465 입력제한 20->32로 수정(leehj)
              // maxLength={32}
              error={camNameLengthErr}
            />
            {/* mantis - 11105, ios 한글 Buffer 이슈수정 (Leehj) */}
            {browser?.os === 'iOS' && (
              <Input
                inputID='hiddenInput'
                style={{ position: 'absolute', left: -9999, top: -9999 }}
              />
            )}

            <div
              style={{
                padding: '4px 13px',
                display: 'flex',
                justifyContent: camNameLengthErr ? 'flex-start' : 'flex-end',
              }}
            >
              {camNameLengthErr && (
                <Typography
                  category='Default'
                  variant='Caption'
                  htmlColor={LightColors.secondary['11']}
                >
                  {t('Camera name must_2')}
                </Typography>
              )}
              <Typography
                category='Default'
                variant='Caption'
                htmlColor={
                  camNameLengthErr
                    ? LightColors.secondary['11']
                    : LightColors.primary['2']
                }
              >
                {/* mantis - 10465 입력제한 20->32로 수정(leehj) */}
                {devName.length}/32
              </Typography>
            </div>
          </div>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
            }}
          >
            <Typography variant='BodyBold'>{t('Camera Details')}</Typography>
            <Divider />
            <Typography variant='Small'>{t('Model')}</Typography>
            <Typography variant='Caption' htmlColor={LightColors.primary['3']}>
              {camera?.model || '-'}
            </Typography>
            <Divider />
            <Typography variant='Small'>{t('Serial Number')}</Typography>
            <Typography variant='Caption' htmlColor={LightColors.primary['3']}>
              {camera?.psn || '-'}
            </Typography>
            <Divider />
            <Typography variant='Small'>{t('Camera SSID')}</Typography>
            <Typography variant='Caption' htmlColor={LightColors.primary['3']}>
              {camera?.ap_ssid || '-'}
            </Typography>
            <Divider />
            <Typography variant='Small'>{t('Firmware Version')}</Typography>
            <Typography variant='Caption' htmlColor={LightColors.primary['3']}>
              {camera?.fw_ver || '-'}
            </Typography>
            <Divider />
            {camera?.reg_category !== 'wifi' && (
              <>
                <Typography variant='Small'>
                  {t('Internet Connection')}
                </Typography>
                <Typography
                  variant='Caption'
                  htmlColor={LightColors.primary['3']}
                >
                  {camera && getConnectionInfo(camera)}
                </Typography>
                <Divider />
              </>
            )}

            {camera?.battery?.ssid && (
              <>
                <Typography variant='Small'>{t('Paired Battery')}</Typography>
                <Typography
                  variant='Caption'
                  htmlColor={LightColors.primary['3']}
                >
                  {camera?.battery?.ssid || '-'}
                </Typography>
                <Divider />
              </>
            )}
          </div>

          <Modal
            open={isOpenChangeProfileImageModal}
            noPadding
            onClickAway={() => setIsOpenChangeProfileImageModal(false)}
            className={classes.changeProfileImageModal}
            content={
              <ProfileCrop
                onSave={(img) => {
                  setFile(img.file);
                  setProfileImage(img.base);
                  setProfileType('img');
                }}
                onClose={() => setIsOpenChangeProfileImageModal(false)}
                profileImage={tempProfileImage}
              />
            }
          />
          <Modal
            open={isShowWithoutSaveModal}
            onClickAway={() => setIsShowWithoutSaveModal(false)}
            onClickNegative={() => {
              onClose();
              setIsShowWithoutSaveModal(false);
            }}
            onClickPositive={() => {
              setIsShowWithoutSaveModal(false);
              handleSave();
            }}
            heading={t('Do you want_')}
            LButton={t('Leave without saving')}
            RButton={t('Yes')}
            contentClassName={classes.contentNoPadding}
            content={<></>}
          />
          {isShowHelp &&
            !mobile &&
            helpComponent({
              children: (
                <div
                  style={{
                    position: 'absolute',
                    left: helpPosition.x + 32,
                    top: helpPosition.y - 66,
                    paddingLeft: 24,
                    zIndex: 9999,
                    width: 368,
                    height: 400,
                    backgroundSize: 'cover',
                    backgroundImage:
                      'url(/images/profile-edit-help-backdrop.png)',
                  }}
                >
                  {helpContentMarkup}
                </div>
              ),
            })}
          {isShowHelp && mobile && (
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                margin: '24px 16px',
                background: LightColors.primary['0'],
                border: `1px solid ${LightColors.primary['6']}`,
                borderRadius: 12,
              }}
            >
              {helpContentMarkup}
            </div>
          )}
        </>
      }
      RButtonDisabled={!isChangeProfile || camNameLengthErr}
      RButton={t('Save')}
    />
  );
};
